// frontend/src/components/FinanceReport/Accounting/PaymentDetailPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../../api/api";

import { usePaymentData } from "./allocations/usePaymentData";
import {
  API_URL,
  COMMISSION_API,
  ALLOC_DOCS_URL,
  EPS,
} from "./allocations/constants";
import {
  money,
  getOrderId,
  getOrderCode,
  getClientId,
  getOurAllocations,
  requestJson,
  emptyAddDraft,
} from "./allocations/helpers";

import PaymentSummary from "./allocations/PaymentSummary";
import AllocationsTable from "./allocations/AllocationsTable";
import EditAllocationModal from "./allocations/EditAllocationModal";
import DeleteCommissionModal from "./allocations/DeleteCommissionModal";
import DocumentsModal from "./allocations/DocumentsModal";

export default function PaymentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    payment,
    setPayment,
    orders,
    currencies,
    accounts,
    profitAllocations,
    supplierPrepayments,
    loading,
    error,
    reloadPayment,
    reloadDistribution,
  } = usePaymentData(id);

  // ── add row ─────────────────────────────────────────────
  const [addType, setAddType] = useState("allocation");
  const [addDraft, setAddDraft] = useState(emptyAddDraft());
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // ── inline edit (allocations + commissions) ─────────────
  const [editingKind, setEditingKind] = useState(null); // 'allocation'|'commission'
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // ── delete allocation ────────────────────────────────────
  const [modalMode, setModalMode] = useState(null);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [draft, setDraft] = useState({
    orderCode: "",
    amount: "",
    currency: "",
    date: "",
    comment: "",
    payment_agent: "",
    payment_status: "PENDING",
  });
  const [newFiles, setNewFiles] = useState([]);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);

  // ── delete commission ────────────────────────────────────
  const [commissionToDelete, setCommissionToDelete] = useState(null);
  const [commissionDeleting, setCommissionDeleting] = useState(false);
  const [commissionDeleteError, setCommissionDeleteError] = useState("");

  // ── docs modal ───────────────────────────────────────────
  const [docsModal, setDocsModal] = useState({
    open: false,
    item: null,
    type: null,
  });

  useEffect(() => {
    if (payment) {
      setAddDraft((prev) => ({
        ...prev,
        currency:
          prev.currency || (payment.currency ? String(payment.currency) : ""),
      }));
    }
  }, [payment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allocations = payment?.allocations || [];
  const commissions = payment?.commission_allocations || [];
  const clientId = payment?.client ?? null;

  // Все заказы клиента — для валидации при редактировании
  const allClientOrders = useMemo(() => {
    if (!clientId) return orders;
    return orders.filter((o) => String(getClientId(o)) === String(clientId));
  }, [orders, clientId]);

  // Только активные — для datalist (dropdown)
  const activeClientOrders = useMemo(
    () => allClientOrders.filter((o) => o.status !== "completed"),
    [allClientOrders]
  );

  const ordersByCode = useMemo(() => {
    const map = new Map();
    allClientOrders.forEach((o) =>
      map.set(getOrderCode(o).trim().toLowerCase(), o)
    );
    return map;
  }, [allClientOrders]);

  const currenciesById = useMemo(() => {
    const map = {};
    currencies.forEach((c) => (map[c.id] = c));
    return map;
  }, [currencies]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => (map[a.id] = a));
    return map;
  }, [accounts]);

  const paymentCurrency = payment?.currency
    ? currenciesById[payment.currency] || null
    : null;

  const allocationIdSet = useMemo(
    () => new Set(allocations.map((a) => String(a.id))),
    [allocations]
  );

  const profitByAllocation = useMemo(() => {
    const map = {};
    profitAllocations.forEach((p) => {
      const key = String(p.allocation);
      if (!allocationIdSet.has(key)) return;
      map[key] = (map[key] || 0) + Number(p.amount || 0);
    });
    return map;
  }, [profitAllocations, allocationIdSet]);

  const supplierByAllocation = useMemo(() => {
    const map = {};
    supplierPrepayments.forEach((p) => {
      const key = String(p.allocation);
      if (!allocationIdSet.has(key)) return;
      map[key] = (map[key] || 0) + Number(p.amount || 0);
    });
    return map;
  }, [supplierPrepayments, allocationIdSet]);

  const allocationBreakdown = useMemo(() => {
    return allocations.map((a) => {
      const our = Number(a.our_allocated_sum || 0);
      const profit = Number(
        a.profit_allocated_sum ?? profitByAllocation[String(a.id)] ?? 0
      );
      const supplier = Number(
        a.supplier_prepaid_sum ?? supplierByAllocation[String(a.id)] ?? 0
      );
      const amount = Number(a.amount || 0);
      const distributed = our + profit + supplier;
      const remaining = amount - distributed;
      const complete = Math.abs(remaining) <= EPS;
      return {
        raw: a,
        amount,
        our,
        profit,
        supplier,
        distributed,
        remaining,
        complete,
      };
    });
  }, [allocations, profitByAllocation, supplierByAllocation]);

  const totals = useMemo(
    () =>
      allocationBreakdown.reduce(
        (acc, b) => ({
          our: acc.our + b.our,
          profit: acc.profit + b.profit,
          supplier: acc.supplier + b.supplier,
          allocated: acc.allocated + b.amount,
        }),
        { our: 0, profit: 0, supplier: 0, allocated: 0 }
      ),
    [allocationBreakdown]
  );

  const totalCommissions = Number(payment?.commission_allocated_sum || 0);
  const allocationsSum = totals.allocated;
  const moneySum = Number(payment?.money_sum || 0);
  const remainingSum = moneySum - allocationsSum - totalCommissions;

  const allocationsState =
    allocationBreakdown.length === 0
      ? "empty"
      : allocationBreakdown.every((b) => b.complete)
      ? "complete"
      : "incomplete";

  const tableRows = useMemo(() => {
    const rows = allocationBreakdown.map((b) => ({
      kind: "allocation",
      id: `a-${b.raw.id}`,
      data: b,
    }));
    commissions.forEach((c) =>
      rows.push({ kind: "commission", id: `c-${c.id}`, data: c })
    );
    rows.sort((x, y) => {
      const dx = x.kind === "allocation" ? x.data.raw.date : x.data.date;
      const dy = y.kind === "allocation" ? y.data.raw.date : y.data.date;
      return (dx || "9999").localeCompare(dy || "9999");
    });
    return rows;
  }, [allocationBreakdown, commissions]);

  // ─── add row helpers ────────────────────────────────────
  const setAddField = (field, value) =>
    setAddDraft((c) => ({ ...c, [field]: value }));

  const resetAddDraft = () =>
    setAddDraft({
      ...emptyAddDraft(),
      currency: payment?.currency ? String(payment.currency) : "",
    });

  const buildAllocationPayload = (allocation) => ({
    id: allocation.id,
    order: Number(getOrderId(allocation)),
    amount: String(allocation.amount),
    currency: allocation.currency ?? null,
    date: allocation.date || null,
    comment: allocation.comment || "",
    payment_agent: allocation.payment_agent || "",
    payment_status: allocation.payment_status || "PENDING",
    our_allocations: getOurAllocations(allocation),
  });

  const persistAllocations = async (nextAllocations) => {
    await requestJson(`${API_URL}${id}/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ allocations: nextAllocations }),
    });
    const updated = await requestJson(`${API_URL}${id}/`);
    setPayment(updated);
    return updated;
  };

  // ─── add ────────────────────────────────────────────────
  const handleAdd = async () => {
    setAddError("");
    if (!payment) return;

    const code = addDraft.orderCode.trim().toLowerCase();
    const order = ordersByCode.get(code);
    if (!order) return setAddError("Заказ с таким кодом не найден.");

    const amount = Number(addDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0)
      return setAddError("Введи сумму больше нуля.");

    const currencyId = addDraft.currency ? Number(addDraft.currency) : null;

    if (addType === "allocation") {
      const otherTotal = allocations.reduce(
        (s, i) => s + Number(i.amount || 0),
        0
      );
      if (
        payment.money_sum != null &&
        otherTotal + amount > Number(payment.money_sum) + 0.000001
      ) {
        return setAddError(
          `Сумма назначений превысит платёж ${money(
            payment.money_sum,
            paymentCurrency
          )}.`
        );
      }
      setAddSaving(true);
      try {
        const next = [
          ...allocations.map(buildAllocationPayload),
          {
            order: Number(order.id),
            amount: amount.toFixed(2),
            currency: currencyId,
            date: addDraft.date || null,
            comment: addDraft.comment || "",
            payment_agent: addDraft.payment_agent || "",
            payment_status: addDraft.payment_status || "PENDING",
            our_allocations: [],
          },
        ];
        await persistAllocations(next);
        await reloadDistribution();
        resetAddDraft();
      } catch (err) {
        setAddError(err.message || "Не удалось добавить.");
      } finally {
        setAddSaving(false);
      }
      return;
    }

    // commission
    if (!addDraft.account)
      return setAddError("Выберите счёт компании для комиссии.");
    setAddSaving(true);
    try {
      await requestJson(COMMISSION_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accounting_record: Number(payment.id),
          order: Number(order.id),
          account: Number(addDraft.account),
          amount: amount.toFixed(2),
          currency: currencyId,
          date: addDraft.date || null,
          comment: addDraft.comment || "",
          payment_agent: addDraft.payment_agent || "",
          payment_status: addDraft.payment_status || "PENDING",
        }),
      });
      await reloadPayment();
      resetAddDraft();
    } catch (err) {
      setAddError(err.message || "Не удалось добавить комиссию.");
    } finally {
      setAddSaving(false);
    }
  };

  // ─── inline edit ───────────────────────────────────────
  const cancelEdit = () => {
    setEditingKind(null);
    setEditingId(null);
    setEditDraft(null);
    setEditError("");
  };

  const startEditAllocation = (a) => {
    setEditingKind("allocation");
    setEditingId(a.id);
    setEditDraft({
      orderCode: a.order_code || "",
      amount: String(a.amount ?? ""),
      currency: a.currency ? String(a.currency) : "",
      date: a.date || "",
      comment: a.comment || "",
      payment_agent: a.payment_agent || "",
      payment_status: a.payment_status || "PENDING",
      account: "",
    });
    setEditError("");
  };

  const startEditCommission = (c) => {
    setEditingKind("commission");
    setEditingId(c.id);
    setEditDraft({
      orderCode: c.order_code || "",
      amount: String(c.amount ?? ""),
      currency: c.currency ? String(c.currency) : "",
      date: c.date || "",
      comment: c.comment || "",
      payment_agent: c.payment_agent || "",
      payment_status: c.payment_status || "PENDING",
      account: c.account ? String(c.account) : "",
    });
    setEditError("");
  };

  const saveEditAllocation = async () => {
    setEditError("");
    if (!payment || !editDraft || !editingId) return;

    const a = allocations.find((x) => String(x.id) === String(editingId));
    if (!a) return setEditError("Назначение не найдено.");

    const code = editDraft.orderCode.trim().toLowerCase();
    const order = ordersByCode.get(code);
    if (!order) return setEditError("Заказ с таким кодом не найден.");

    const amount = Number(editDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0)
      return setEditError("Введи сумму больше нуля.");

    const currencyId = editDraft.currency
      ? Number(editDraft.currency)
      : null;

    if (Number(a.our_allocated_sum || 0) > amount) {
      return setEditError(
        "Сумма не может быть меньше уже распределённой суммы наших назначений."
      );
    }

    const otherTotal = allocations
      .filter((x) => String(x.id) !== String(editingId))
      .reduce((s, x) => s + Number(x.amount || 0), 0);
    if (
      payment.money_sum != null &&
      otherTotal + amount > Number(payment.money_sum) + 0.000001
    ) {
      return setEditError("Превысит сумму платежа.");
    }

    setEditSaving(true);
    try {
      const next = allocations.map((item) =>
        String(item.id) === String(editingId)
          ? {
              id: item.id,
              order: Number(order.id),
              amount: amount.toFixed(2),
              currency: currencyId,
              date: editDraft.date || null,
              comment: editDraft.comment || "",
              payment_agent: editDraft.payment_agent || "",
              payment_status: editDraft.payment_status || "PENDING",
              our_allocations: getOurAllocations(item),
            }
          : buildAllocationPayload(item)
      );
      await persistAllocations(next);
      await reloadDistribution();
      cancelEdit();
    } catch (err) {
      setEditError(err.message || "Не удалось сохранить.");
    } finally {
      setEditSaving(false);
    }
  };

  const saveEditCommission = async () => {
    setEditError("");
    if (!editDraft || !editingId) return;

    const c = commissions.find((x) => String(x.id) === String(editingId));
    if (!c) return setEditError("Комиссия не найдена.");

    const code = editDraft.orderCode.trim().toLowerCase();
    const order = ordersByCode.get(code);
    if (!order) return setEditError("Заказ с таким кодом не найден.");

    if (!editDraft.account)
      return setEditError("Выберите счёт компании.");

    const amount = Number(editDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0)
      return setEditError("Введи сумму больше нуля.");

    setEditSaving(true);
    try {
      await requestJson(`${COMMISSION_API}${editingId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order: Number(order.id),
          account: Number(editDraft.account),
          amount: amount.toFixed(2),
          currency: editDraft.currency ? Number(editDraft.currency) : null,
          date: editDraft.date || null,
          comment: editDraft.comment || "",
          payment_agent: editDraft.payment_agent || "",
          payment_status: editDraft.payment_status || "PENDING",
        }),
      });
      await reloadPayment();
      cancelEdit();
    } catch (err) {
      setEditError(err.message || "Не удалось сохранить комиссию.");
    } finally {
      setEditSaving(false);
    }
  };

  const saveEdit = async () => {
    if (editingKind === "allocation") return saveEditAllocation();
    return saveEditCommission();
  };

  // ─── delete allocation ─────────────────────────────────
  const openDeleteModal = (a) => {
    setSelectedAllocation(a);
    setModalError("");
    setModalMode("delete");
  };

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
    setSelectedAllocation(null);
    setModalError("");
  };

  const deleteAllocation = async () => {
    if (!payment || !selectedAllocation) return;
    setSaving(true);
    setModalError("");
    try {
      const next = allocations
        .filter((x) => String(x.id) !== String(selectedAllocation.id))
        .map(buildAllocationPayload);
      await persistAllocations(next);
      await reloadDistribution();
      setModalMode(null);
      setSelectedAllocation(null);
    } catch (err) {
      setModalError(err.message || "Не удалось удалить.");
    } finally {
      setSaving(false);
    }
  };

  // ─── delete commission ────────────────────────────────
  const deleteCommission = async () => {
    if (!commissionToDelete) return;
    setCommissionDeleting(true);
    setCommissionDeleteError("");
    try {
      const r = await authFetch(
        `${COMMISSION_API}${commissionToDelete.id}/`,
        { method: "DELETE" }
      );
      if (!r.ok) throw new Error(await r.text());
      setCommissionToDelete(null);
      await reloadPayment();
      await reloadDistribution();
    } catch (err) {
      setCommissionDeleteError(err.message || "Не удалось удалить комиссию.");
    } finally {
      setCommissionDeleting(false);
    }
  };

  if (loading)
    return <div className="p-8 text-gray-500">Загрузка платежа...</div>;
  if (error || !payment)
    return <div className="p-8 text-red-600">{error || "Платёж не найден"}</div>;

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <button
          type="button"
          onClick={() => navigate("/accounting")}
          className="mb-5 text-sm text-slate-500 hover:text-blue-600"
        >
          ← Все платежи
        </button>

        <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">Учёт платежей</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">
              Платёж №{payment.id}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {payment.date || "Дата не указана"}
              {payment.payment_agent ? ` · ${payment.payment_agent}` : ""}
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              payment.payment_status === "PAID"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {payment.payment_status === "PAID" ? "Оплачен" : "Ожидает оплаты"}
          </span>
        </header>

        <PaymentSummary
          currency={paymentCurrency}
          moneySum={moneySum}
          allocationsSum={allocationsSum}
          allocationsState={allocationsState}
          commissionsSum={totalCommissions}
          remainingSum={remainingSum}
        />

        <AllocationsTable
          paymentId={payment.id}
          tableRows={tableRows}
          allocationsCount={allocationBreakdown.length}
          commissionsCount={commissions.length}
          currenciesById={currenciesById}
          accountsById={accountsById}
          paymentCurrency={paymentCurrency}
          onDeleteAllocation={openDeleteModal}
          onDeleteCommission={(c) => {
            setCommissionDeleteError("");
            setCommissionToDelete(c);
          }}
          onOpenDocs={(payload) => setDocsModal({ open: true, ...payload })}
          clientOrders={activeClientOrders}
          ordersByCode={ordersByCode}
          currencies={currencies}
          accounts={accounts}
          addType={addType}
          setAddType={(v) => {
            setAddType(v);
            setAddError("");
          }}
          addDraft={addDraft}
          setAddField={setAddField}
          addSaving={addSaving}
          addError={addError}
          onAdd={handleAdd}
          editingKind={editingKind}
          editingId={editingId}
          editDraft={editDraft}
          setEditDraft={setEditDraft}
          editError={editError}
          editSaving={editSaving}
          onStartEditAllocation={startEditAllocation}
          onStartEditCommission={startEditCommission}
          onSaveEdit={saveEdit}
          onCancelEdit={cancelEdit}
        />

        {payment.comment && (
          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-semibold text-slate-800">Комментарий</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">
              {payment.comment}
            </p>
          </section>
        )}
      </div>

      {/* Модалка теперь ТОЛЬКО для удаления allocation */}
      <EditAllocationModal
        mode={modalMode === "delete" ? "delete" : null}
        draft={draft}
        setDraft={setDraft}
        selectedAllocation={selectedAllocation}
        newFiles={newFiles}
        setNewFiles={setNewFiles}
        clientOrders={activeClientOrders}
        currencies={currencies}
        currenciesById={currenciesById}
        paymentCurrency={paymentCurrency}
        modalError={modalError}
        saving={saving}
        onClose={closeModal}
        onSave={null}
        onDelete={deleteAllocation}
      />

      <DocumentsModal
        open={docsModal.open}
        item={docsModal.item}
        type={docsModal.type}
        onClose={() => setDocsModal({ open: false, item: null, type: null })}
        onChanged={reloadPayment}
      />

      <DeleteCommissionModal
        commission={commissionToDelete}
        currenciesById={currenciesById}
        paymentCurrency={paymentCurrency}
        error={commissionDeleteError}
        deleting={commissionDeleting}
        onClose={() => {
          setCommissionToDelete(null);
          setCommissionDeleteError("");
        }}
        onConfirm={deleteCommission}
      />
    </main>
  );
}